import { useState } from "react";
import axios from "axios";
import {
  AuthContextValue,
  SignInProps,
  UserInfo,
  LoginResp,
} from "./AuthInterface";
import { useNotify } from "../../Hooks/useNotify";
import { Post } from "../../Fetch";
import { logout } from "../../utils";
import { useSearchParams } from "react-router-dom";
import { markFreshLogin, clearFreshLoginFlag } from "../../Utils/authSession";

export const useProvideAuth = (): AuthContextValue => {
  const { ErrorNotify } = useNotify();
  const [searchParams] = useSearchParams();

  const [authenticating, setAuthenticating] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);

  const verifyLogin = () => {
    setAuthenticating(true);

    if (searchParams.get("swayamuser") || searchParams.get("bitslawuser")) {
      setIsAuthenticated(false);
      setAuthenticating(false);
    } else {
      const rt = localStorage.getItem("rt");
      if (rt) {
        if (localStorage.getItem("mustChangePassword") === "true") {
          setMustChangePassword(true);
        }
        setIsAuthenticated(true);
        setAuthenticating(false);
      } else {
        setIsAuthenticated(false);
        setAuthenticating(false);
      }
    }
  };

  const verifySession = () => {
    setAuthenticating(true);
    axios
      .request({
        url: "/api/user_info",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })
      .then((res) => {
        const resp = res.data as UserInfo;
        if (resp?.status_code === "01") {
          setIsAuthenticated(true);
          setAuthenticating(false);
        } else {
          setIsAuthenticated(false);
          setAuthenticating(false);
          ErrorNotify(
            {
              title: "Access unauthorized",
              desc: "You've been logged out. Please login again.",
            },
            false
          );
        }
      })
      .catch((err) => {
        setIsAuthenticated(false);
        setAuthenticating(false);
        ErrorNotify(err);
      });
  };

  const signin = async ({ username, password }: SignInProps): Promise<void> => {
    Post<LoginResp>({
      url: "/auth/Login",
      data: { username, password },
    })
      .then((res) => {
        if (res) {
          localStorage.setItem("token", res.token);
          localStorage.setItem("rt", res.refreshtoken);
          // Always reset the flag first so a previous session's value never bleeds into a new login
          localStorage.removeItem("mustChangePassword");
          if (res.mustChangePassword) {
            localStorage.setItem("mustChangePassword", "true");
            setMustChangePassword(true);
          } else {
            setMustChangePassword(false);
          }
          setIsAuthenticated(true);
          markFreshLogin();
        }
      })
      .catch((err) => {
        ErrorNotify(err);
      });
  };

  const signout = () => {
    Post<unknown>({
      url: "/auth/Logout",
      data: {},
    });
    setIsAuthenticated(false);
    setMustChangePassword(false);
    localStorage.removeItem("mustChangePassword");
    clearFreshLoginFlag();
    logout();
  };

  return {
    signin,
    signout,
    authenticating,
    isAuthenticated,
    mustChangePassword,
    setMustChangePassword,
    verifyLogin,
    verifySession,
  };
};
